import React, { useEffect, useMemo, useState } from 'react';
import { Button, Message, Placeholder, SelectPicker } from 'rsuite';
import { Button as NsiButton, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import ProfilePhoto from '../components/employees/ProfilePhoto';
import ApprovalReviewModal from '../components/timeoff/ApprovalReviewModal';
import ExpenseMarkPaidModal from '../components/expenses/ExpenseMarkPaidModal';
import {
  useApproveExpense,
  useApproveTimeOffRequest,
  useCommentExpense,
  useCommentTimeOffRequest,
  useExpenses,
  useMarkExpensePaid,
  useRejectExpense,
  useRejectTimeOffRequest,
  useTimeOffRequests,
} from '../hooks/useQueries';
import { toDateOnlyString } from '../utils/payPeriods';
import './ApprovalsGridPage.css';

const STALE_APPROVAL_TOAST = 'This request has been modified. The approvals list has been refreshed.';

// The server refuses to approve, reject or comment on a request that is no
// longer pending; any of those means someone else got there first, so match the
// shared prefix rather than each verb.
const isStaleTimeOffDecisionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('only pending requests can be')
    || message.includes('request not found')
  );
};

const isStaleExpenseDecisionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('only pending expenses can be')
    || message.includes('expense not found')
  );
};

const isPendingStatus = (value) => String(value || '').toLowerCase() === 'pending';

const showApprovalToast = (type, content) => showToast(type, content, { duration: 5000 });
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = DATE_ONLY_PATTERN.exec(trimmed);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      const localDate = new Date(year, monthIndex, day);
      return localDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const getEmployeeDisplay = (rowOrEmployee) => {
  const first = rowOrEmployee?.first_name || rowOrEmployee?.employee_first_name || '';
  const last = rowOrEmployee?.last_name || rowOrEmployee?.employee_last_name || '';
  const email = rowOrEmployee?.email || rowOrEmployee?.employee_email || '';
  const name = `${first} ${last}`.trim();
  const initials = `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase() || 'U';

  return { first, last, email, name: name || 'Unknown Employee', initials };
};

const capitalize = (value) => {
  const s = String(value || '').trim();
  if (!s) return '';
  return s.slice(0, 1).toUpperCase() + s.slice(1);
};

const formatOutstandingItem = (item) => {
  const kind = item?.__approvalKind;

  if (kind === 'expense') {
    const vendor = String(item?.vendor_name || '').trim() || 'Expense';
    const amount = formatCurrency(item?.amount);
    const when = formatDate(item?.purchase_date);
    const meta = [amount, when].filter(Boolean).join(' • ');
    return {
      kindLabel: 'Expense',
      title: vendor,
      meta,
    };
  }

  const requestType = capitalize(item?.request_type) || 'Time off';
  const start = formatDate(item?.start_date);
  const end = formatDate(item?.end_date);
  const meta = `${start}${end ? ` – ${end}` : ''}`.trim();
  return {
    kindLabel: 'Time Off',
    title: requestType,
    meta,
  };
};

export default function ApprovalsGridPage() {
  const auth = useAuth();
  const currentEmployeeId = String(auth.employee?.id || '');

  const canApproveExpenses = auth.isFinance() || auth.isAdmin() || auth.hasPermission('approve_expenses');
  const canApproveTimeOff = auth.isApprover() || auth.isAdmin() || auth.hasPermission('approve_time_off');
  const canManagePayroll = auth.isFinance() || auth.isAdmin() || auth.hasPermission('manage_payroll');
  const canViewPage = canApproveExpenses || canApproveTimeOff;

  const expensesQuery = useExpenses({ enabled: canApproveExpenses });
  const markExpensePaidMutation = useMarkExpensePaid();
  const timeOffQuery = useTimeOffRequests({ enabled: canApproveTimeOff });

  const approveExpenseMutation = useApproveExpense();
  const rejectExpenseMutation = useRejectExpense();
  const approveTimeOffMutation = useApproveTimeOffRequest();
  const rejectTimeOffMutation = useRejectTimeOffRequest();
  const commentExpenseMutation = useCommentExpense();
  const commentTimeOffMutation = useCommentTimeOffRequest();

  const filterOptions = useMemo(() => {
    const opts = [];
    if (canApproveExpenses && canApproveTimeOff) {
      opts.push({ label: 'All', value: 'all' });
    }
    if (canApproveTimeOff) opts.push({ label: 'Time Off', value: 'timeoff' });
    if (canApproveExpenses) opts.push({ label: 'Expenses', value: 'expense' });
    return opts;
  }, [canApproveExpenses, canApproveTimeOff]);

  const [typeFilter, setTypeFilter] = useState('all');
  const [hasUserSelectedFilter, setHasUserSelectedFilter] = useState(false);

  useEffect(() => {
    if (filterOptions.length === 0) return;
    const stillValid = filterOptions.some((o) => o.value === typeFilter);
    if (!stillValid) setTypeFilter(filterOptions[0].value);
    if (!hasUserSelectedFilter && filterOptions.some((o) => o.value === 'all') && typeFilter !== 'all') {
      setTypeFilter('all');
    }
  }, [filterOptions, hasUserSelectedFilter, typeFilter]);

  const pendingExpenseItems = useMemo(() => {
    if (!canApproveExpenses) return [];
    const rows = expensesQuery.data || [];
    return rows
      .filter((e) => {
        if (currentEmployeeId && String(e?.employee_id) === currentEmployeeId) return false;
        return String(e?.status || '').toLowerCase() === 'pending';
      })
      .map((e) => ({ ...e, __approvalKind: 'expense' }));
  }, [canApproveExpenses, currentEmployeeId, expensesQuery.data]);

  const pendingTimeOffItems = useMemo(() => {
    if (!canApproveTimeOff) return [];
    const rows = timeOffQuery.data || [];
    return rows
      .filter((r) => {
        if (currentEmployeeId && String(r?.employee_id) === currentEmployeeId) return false;
        return String(r?.status || '').toLowerCase() === 'pending';
      })
      .map((r) => ({ ...r, __approvalKind: 'timeoff' }));
  }, [canApproveTimeOff, currentEmployeeId, timeOffQuery.data]);

  const pendingApprovals = useMemo(() => {
    if (typeFilter === 'expense') return pendingExpenseItems;
    if (typeFilter === 'timeoff') return pendingTimeOffItems;
    return [...pendingTimeOffItems, ...pendingExpenseItems];
  }, [pendingExpenseItems, pendingTimeOffItems, typeFilter]);

  const employees = useMemo(() => {
    const map = new Map();

    const add = (row) => {
      const employeeId = row?.employee_id;
      if (!employeeId) return;
      const key = String(employeeId);
      if (!map.has(key)) {
        const display = getEmployeeDisplay(row);
        map.set(key, {
          id: key,
          first_name: display.first,
          last_name: display.last,
          email: display.email,
          name: display.name,
          initials: display.initials,
        });
      }
    };

    // Build employee list from pending approvals only.
    pendingApprovals.forEach(add);

    return Array.from(map.values());
  }, [pendingApprovals]);

  const employeeCards = useMemo(() => {
    const byEmployee = new Map();
    for (const emp of employees) {
      byEmployee.set(String(emp.id), {
        employee: emp,
        approvals: [],
      });
    }

    const addApproval = (employeeId, item) => {
      const key = String(employeeId);
      if (!byEmployee.has(key)) {
        const display = getEmployeeDisplay(item);
        byEmployee.set(key, {
          employee: {
            id: key,
            first_name: display.first,
            last_name: display.last,
            email: display.email,
            name: display.name,
            initials: display.initials,
          },
          approvals: [],
        });
      }
      byEmployee.get(key).approvals.push(item);
    };

    pendingApprovals.forEach((item) => addApproval(item.employee_id, item));

    const cards = Array.from(byEmployee.values()).map((row) => {
      const approvals = row.approvals || [];
      return {
        employee: row.employee,
        approvals,
        outstandingCount: approvals.length,
      };
    });

    cards.sort((a, b) => {
      const byOutstanding = (b.outstandingCount || 0) - (a.outstandingCount || 0);
      if (byOutstanding !== 0) return byOutstanding;
      return String(a.employee?.name || '').localeCompare(String(b.employee?.name || ''));
    });

    return cards;
  }, [employees, pendingApprovals]);

  const anyOutstanding = useMemo(
    () => employeeCards.some((c) => (c.outstandingCount || 0) > 0),
    [employeeCards]
  );

  const [activeEmployeeId, setActiveEmployeeId] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const [activeRecordType, setActiveRecordType] = useState(null);
  // Held apart from activeRecord so the approval details the payment-date
  // dialog was opened from stay on screen behind it.
  const [paidExpense, setPaidExpense] = useState(null);
  const [paidOnDate, setPaidOnDate] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const activeEmployeeCard = useMemo(() => {
    if (!activeEmployeeId) return null;
    return employeeCards.find((card) => String(card?.employee?.id) === String(activeEmployeeId)) || null;
  }, [activeEmployeeId, employeeCards]);
  const activeEmployeeShown = useLastPresent(activeEmployeeCard); // keeps the dialog steady while it fades out

  const openEmployee = (card) => {
    setActiveEmployeeId(String(card?.employee?.id || ''));
  };

  const closeEmployee = () => {
    setActiveEmployeeId(null);
  };

  const openReview = async (row) => {
    if (currentEmployeeId && String(row?.employee_id) === currentEmployeeId) return;

    const recordType = row?.__approvalKind === 'expense' ? 'expense' : 'timeoff';
    let latestRows = recordType === 'expense' ? (expensesQuery.data || []) : (timeOffQuery.data || []);

    try {
      const refreshed = recordType === 'expense'
        ? await expensesQuery.refetch()
        : await timeOffQuery.refetch();
      latestRows = refreshed?.data || latestRows;
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to refresh approvals'));
      return;
    }

    const latest = latestRows.find((item) => String(item?.id) === String(row?.id));
    if (!latest || !isPendingStatus(latest.status)) {
      showApprovalToast('warning', STALE_APPROVAL_TOAST);
      return;
    }

    setActiveRecord({ ...latest, __approvalKind: recordType });
    setActiveRecordType(recordType);
    setReviewOpen(true);
  };

  // Recording the payment date without leaving for the payroll view, for the
  // same person who is allowed to do it there.
  const openMarkPaid = (expense) => {
    if (!expense?.id) return;
    setPaidExpense(expense);
    setPaidOnDate(new Date());
  };

  const closeMarkPaid = () => {
    setPaidExpense(null);
    setPaidOnDate(null);
  };

  const submitMarkPaid = async () => {
    if (!paidExpense?.id || !paidOnDate) return;
    const paidOn = toDateOnlyString(paidOnDate);

    try {
      await markExpensePaidMutation.mutateAsync({ id: paidExpense.id, paid_on: paidOn });
      // The details are still open behind this dialog and would go on
      // describing an unpaid expense otherwise.
      setActiveRecord((prev) => (
        prev && String(prev.id) === String(paidExpense.id) ? { ...prev, paid_on: paidOn } : prev
      ));
      closeMarkPaid();
      showApprovalToast('success', 'Expense marked paid');
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to mark this expense paid'));
      await expensesQuery.refetch();
    }
  };

  const closeReview = () => {
    setReviewOpen(false);
    setActiveRecord(null);
    setActiveRecordType(null);
  };

  const submitApprove = async (approval_reason) => {
    if (!activeRecord?.id) return;
    try {
      if (activeRecordType === 'expense') {
        await approveExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      } else {
        await approveTimeOffMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      }
      closeReview();
    } catch (error) {
      const stale = activeRecordType === 'expense'
        ? isStaleExpenseDecisionError(error)
        : isStaleTimeOffDecisionError(error);

      if (stale) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        closeReview();

        if (activeRecordType === 'expense') {
          await expensesQuery.refetch();
        } else {
          await timeOffQuery.refetch();
        }
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to approve request'));
    }
  };

  // Saves the comment and leaves the record pending, so it stays in the queue.
  const submitComment = async (approval_reason) => {
    if (!activeRecord?.id) return;
    try {
      if (activeRecordType === 'expense') {
        await commentExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      } else {
        await commentTimeOffMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      }
      showApprovalToast('success', 'Comment saved');
      closeReview();
    } catch (error) {
      const stale = activeRecordType === 'expense'
        ? isStaleExpenseDecisionError(error)
        : isStaleTimeOffDecisionError(error);

      if (stale) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        closeReview();

        if (activeRecordType === 'expense') {
          await expensesQuery.refetch();
        } else {
          await timeOffQuery.refetch();
        }
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to save comment'));
    }
  };

  const submitReject = async (approval_reason) => {
    if (!activeRecord?.id) return;
    try {
      if (activeRecordType === 'expense') {
        await rejectExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      } else {
        await rejectTimeOffMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      }
      closeReview();
    } catch (error) {
      const stale = activeRecordType === 'expense'
        ? isStaleExpenseDecisionError(error)
        : isStaleTimeOffDecisionError(error);

      if (stale) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        closeReview();

        if (activeRecordType === 'expense') {
          await expensesQuery.refetch();
        } else {
          await timeOffQuery.refetch();
        }
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to reject request'));
    }
  };

  if (auth.isLoading || auth.isResolving) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (!canViewPage) {
    return (
      <Message type="error" header="Access denied" showIcon>
        You do not have permission to view approvals.
      </Message>
    );
  }

  if ((canApproveExpenses && expensesQuery.isLoading) || (canApproveTimeOff && timeOffQuery.isLoading)) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (expensesQuery.error || timeOffQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(expensesQuery.error?.message || timeOffQuery.error?.message || expensesQuery.error || timeOffQuery.error)}
      </Message>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h3 className="m-0">Approvals (Grid)</h3>

        <div className="d-flex align-items-end gap-2 flex-wrap">
          {filterOptions.length > 1 ? (
            <div>
              <div className="text-muted small mb-1">Filter</div>
              <SelectPicker
                data={filterOptions}
                value={typeFilter}
                onChange={(nextValue) => {
                  setHasUserSelectedFilter(true);
                  setTypeFilter(nextValue || filterOptions[0]?.value || 'all');
                }}
                cleanable={false}
                searchable={false}
                style={{ width: 180 }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {!anyOutstanding ? (
        <Message type="info" showIcon>
          No approvals to respond to!
        </Message>
      ) : null}

      {anyOutstanding ? (
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3">
          {employeeCards.map((card) => {
          const emp = card.employee;
          const hasOutstanding = (card.outstandingCount || 0) > 0;
          const borderClass = hasOutstanding ? 'border-danger' : 'border-success';

          return (
            <div className="col" key={String(emp?.id)}>
              <div
                className={`card h-100 approvals-employee-card approvals-employee-card--clickable border border-2 ${borderClass}`}
                role="button"
                tabIndex={0}
                onClick={() => openEmployee(card)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openEmployee(card);
                  }
                }}
              >
                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-center" style={{ marginBottom: 10 }}>
                    <ProfilePhoto employeeId={emp?.id} size="medium" fallbackInitials={emp?.initials} />
                  </div>

                  <div className="text-center fw-semibold" style={{ lineHeight: 1.2 }}>
                    {emp?.name}
                  </div>

                  {emp?.email ? (
                    <div className="text-center text-muted" style={{ fontSize: 13, marginTop: 4 }}>
                      {emp.email}
                    </div>
                  ) : null}

                  <div className="mt-3 approvals-employee-outstanding">
                    <div className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
                      Outstanding ({card.outstandingCount})
                    </div>

                    {card.outstandingCount > 0 ? (
                      <div className="approvals-outstanding-list">
                        {card.approvals.slice(0, 4).map((item) => {
                          const view = formatOutstandingItem(item);
                          const rowKey = `${String(item?.__approvalKind || 'timeoff')}:${String(item?.id || '')}`;

                          return (
                            <div key={rowKey} className="approvals-outstanding-row">
                              <div className="approvals-outstanding-kind">{view.kindLabel}</div>
                              <div className="approvals-outstanding-info">
                                <div className="approvals-outstanding-title" title={view.title}>{view.title}</div>
                                {view.meta ? (
                                  <div className="approvals-outstanding-meta" title={view.meta}>{view.meta}</div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}

                        {card.outstandingCount > 4 ? (
                          <div className="approvals-outstanding-more">
                            and {card.outstandingCount - 4} more…
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        No outstanding approvals.
                      </div>
                    )}
                  </div>

                  <div className="mt-auto pt-3 d-flex justify-content-center">
                    <Button size="xs" appearance="primary" disabled={!hasOutstanding}>
                      View approvals
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      ) : null}

      <Modal open={!!activeEmployeeId} onClose={closeEmployee} size="md">
        <Modal.Header>
          <Modal.Title>
            {activeEmployeeShown?.employee?.name || 'Employee'} approvals
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(activeEmployeeShown?.approvals || []).length === 0 ? (
            <div className="text-muted">No outstanding approvals.</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {(activeEmployeeShown?.approvals || []).map((item) => {
                const key = `${String(item?.__approvalKind || 'timeoff')}:${item?.id}`;
                const view = formatOutstandingItem(item);

                return (
                  <div key={key} className="d-flex justify-content-between align-items-center gap-3 border rounded p-2">
                    <div style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-start gap-2" style={{ minWidth: 0 }}>
                        <div className="approvals-outstanding-kind" style={{ marginTop: 2 }}>
                          {view.kindLabel}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="fw-semibold" style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {view.title}
                          </div>
                          {view.meta ? (
                            <div className="text-muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {view.meta}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <NsiButton
                      size="sm"
                      variant="primary"
                      onClick={() => openReview(item)}
                      disabled={String(item?.status || '').toLowerCase() !== 'pending'}
                    >
                      Review
                    </NsiButton>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeEmployee}>Close</NsiButton>
        </Modal.Footer>
      </Modal>

      <ApprovalReviewModal
        open={reviewOpen}
        onClose={closeReview}
        record={activeRecord}
        recordType={activeRecordType}
        onApprove={submitApprove}
        onReject={submitReject}
        onSaveComment={submitComment}
        onMarkPaid={activeRecordType === 'expense' && canManagePayroll ? openMarkPaid : undefined}
        loading={
          approveExpenseMutation.isPending ||
          approveTimeOffMutation.isPending ||
          rejectExpenseMutation.isPending ||
          rejectTimeOffMutation.isPending
        }
        commentLoading={commentExpenseMutation.isPending || commentTimeOffMutation.isPending}
        markPaidLoading={markExpensePaidMutation.isPending}
      />

      <ExpenseMarkPaidModal
        open={Boolean(paidExpense)}
        value={paidOnDate}
        onChange={setPaidOnDate}
        onSubmit={submitMarkPaid}
        onClose={closeMarkPaid}
        loading={markExpensePaidMutation.isPending}
      />
    </div>
  );
}
